/**
 * פרטי הצעה — TASKS 5.6, PRD 8.2. צפייה, הורדת PDF, שליחה מחדש בדוא"ל,
 * שליחה ידנית בוואטסאפ (5.3), וסטטוס מסירה משני הערוצים.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AdminNav } from './AdminNav.js';
import { AdminPageHeader } from './AdminPageHeader.js';
import { isAdminApiError } from './adminApi.js';
import { StatusMessage } from './StatusMessage.js';
import {
  getQuote,
  quotePdfUrl,
  resendQuoteEmail,
  type EmailDelivery,
  type PriceBasis,
  type QuoteDetail,
  type QuoteItemDetail,
  type WhatsappDelivery,
} from './quotesApi.js';
import { WhatsAppSendButton } from './WhatsAppSendButton.js';

const PRICE_BASIS_LABEL: Record<PriceBasis, string> = {
  base: 'מחיר בסיס',
  customer: 'מחיר מיוחד',
  manual: 'ידני',
};

const EMAIL_STATUS_LABEL: Record<EmailDelivery['status'], string> = {
  queued: 'ממתין',
  sent: 'נשלח',
  failed: 'נכשל',
  pending_retry: 'ממתין לניסיון חוזר',
};

const WHATSAPP_STATUS_LABEL: Record<WhatsappDelivery['status'], string> = {
  queued: 'ממתין',
  sent: 'נשלח',
  delivered: 'נמסר',
  read: 'נקרא',
  failed: 'נכשל',
  pending_retry: 'ממתין לניסיון חוזר',
};

const RECIPIENT_KIND_LABEL: Record<EmailDelivery['recipient_kind'], string> = {
  customer: 'ללקוח',
  owner: 'עותק לטל',
};

export function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const quoteId = Number(id);

  const [quote, setQuote] = useState<QuoteDetail | null>(null);
  const [items, setItems] = useState<QuoteItemDetail[]>([]);
  const [emailDeliveries, setEmailDeliveries] = useState<EmailDelivery[]>([]);
  const [whatsappDeliveries, setWhatsappDeliveries] = useState<WhatsappDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [resending, setResending] = useState(false);
  const [resendResult, setResendResult] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  async function refetch() {
    const result = await getQuote(quoteId);
    setQuote(result.quote);
    setItems(result.items);
    setEmailDeliveries(result.emailDeliveries);
    setWhatsappDeliveries(result.whatsappDeliveries);
  }

  useEffect(() => {
    if (!Number.isInteger(quoteId) || quoteId <= 0) return;
    let cancelled = false;
    setLoading(true);
    refetch()
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(isAdminApiError(err) ? err.message : 'שגיאה בטעינת ההצעה.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [quoteId]);

  async function handleResend() {
    setResendError(null);
    setResendResult(null);
    setResending(true);
    try {
      await resendQuoteEmail(quoteId);
      setResendResult('הדוא"ל נשלח מחדש - אותו PDF, לא הופק מחדש.');
      await refetch();
    } catch (err) {
      setResendError(isAdminApiError(err) ? err.message : 'שגיאה בשליחה מחדש.');
    } finally {
      setResending(false);
    }
  }

  if (loading) {
    return (
      <>
        <AdminNav />
        <p className="text-muted p-8">טוען…</p>
      </>
    );
  }

  if (loadError || !quote) {
    return (
      <>
        <AdminNav />
        <p role="alert" className="text-danger p-8">
          {loadError ?? 'ההצעה לא נמצאה.'}
        </p>
      </>
    );
  }

  return (
    <>
      <AdminNav />
      <main className="mx-auto max-w-3xl px-6 py-12">
        <p className="text-muted text-sm">
          <Link to="/admin/quotes">ארכיון הצעות</Link> ↞ {quote.quote_number}
        </p>
        <div className="mt-1">
          <AdminPageHeader title={`הצעה ${quote.quote_number}`} />
        </div>

        <section
          className="border-line bg-surface mt-6 rounded-lg border p-6"
          aria-labelledby="quote-h"
        >
          <h2 id="quote-h" className="text-lg font-semibold">
            פרטי ההצעה
          </h2>
          <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted">לקוח</dt>
              <dd>
                <Link to={`/admin/customers/${quote.customer_id}`}>{quote.customer_full_name}</Link>
              </dd>
            </div>
            <div>
              <dt className="text-muted">טלפון</dt>
              <dd className="ltr-inline">{quote.customer_phone_e164}</dd>
            </div>
            <div>
              <dt className="text-muted">דוא&quot;ל</dt>
              <dd className="ltr-inline">{quote.customer_email ?? 'אין'}</dd>
            </div>
            <div>
              <dt className="text-muted">מקור</dt>
              <dd>{quote.source === 'site' ? 'אתר' : 'ידני'}</dd>
            </div>
            <div>
              <dt className="text-muted">מקור מחיר</dt>
              <dd>{PRICE_BASIS_LABEL[quote.price_basis]}</dd>
            </div>
            <div>
              <dt className="text-muted">נוצרה</dt>
              <dd>{new Date(quote.created_at).toLocaleDateString('he-IL')}</dd>
            </div>
            <div>
              <dt className="text-muted">תוקף עד</dt>
              <dd>{new Date(quote.valid_until).toLocaleDateString('he-IL')}</dd>
            </div>
          </dl>

          <table className="mt-5 w-full border-collapse text-sm">
            <caption className="sr-only">שורות ההצעה</caption>
            <thead>
              <tr className="border-line border-b text-start">
                <th scope="col" className="p-2 text-start">
                  מק&quot;ט
                </th>
                <th scope="col" className="p-2 text-start">
                  תיאור
                </th>
                <th scope="col" className="p-2 text-start">
                  כמות
                </th>
                <th scope="col" className="p-2 text-start">
                  מחיר יחידה
                </th>
                <th scope="col" className="p-2 text-start">
                  סה&quot;כ
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-line border-b">
                  <td className="p-2">
                    <span className="ltr-inline">{item.sku_snapshot}</span>
                  </td>
                  <td className="p-2">
                    {item.name_snapshot}
                    {item.price_source === 'manual' && (
                      <span className="text-muted"> (מחיר ידני)</span>
                    )}
                    {Boolean(item.is_estimate) && <span className="text-muted"> *משוער</span>}
                    {Boolean(item.is_price_on_request) && (
                      <span className="text-muted"> (לפי בקשה)</span>
                    )}
                  </td>
                  <td className="tabular p-2">{item.qty}</td>
                  <td className="tabular p-2">
                    {item.is_price_on_request ? 'לפי בקשה' : `₪${item.unit_price}`}
                  </td>
                  <td className="tabular p-2">
                    {item.is_price_on_request ? 'לפי בקשה' : `₪${item.line_total}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <dl className="mt-4 space-y-1 text-sm">
            <div className="flex justify-between">
              <dt>לפני מע&quot;מ</dt>
              <dd className="tabular">₪{quote.subtotal}</dd>
            </div>
            <div className="flex justify-between">
              <dt>מע&quot;מ ({quote.vat_rate}%)</dt>
              <dd className="tabular">₪{quote.vat_amount}</dd>
            </div>
            <div className="flex justify-between font-semibold">
              <dt>סה&quot;כ</dt>
              <dd className="tabular">₪{quote.total}</dd>
            </div>
          </dl>
        </section>

        <section
          className="border-line bg-surface mt-6 rounded-lg border p-6"
          aria-labelledby="actions-h"
        >
          <h2 id="actions-h" className="text-lg font-semibold">
            פעולות
          </h2>

          {quote.pdf_path ? (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <a
                href={quotePdfUrl(quote.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                צפייה / הורדת PDF
              </a>
              <button
                type="button"
                className="btn btn-cta"
                disabled={resending}
                onClick={() => void handleResend()}
              >
                {resending ? 'שולח…' : 'שליחה מחדש בדוא"ל'}
              </button>
              <WhatsAppSendButton
                quoteId={quote.id}
                quoteNumber={quote.quote_number}
                onSent={() => void refetch()}
              />
            </div>
          ) : (
            <p className="text-muted mt-3 text-sm">טרם הופק PDF להצעה זו.</p>
          )}

          {resendResult && <StatusMessage className="mt-3 text-sm">{resendResult}</StatusMessage>}
          {resendError && (
            <p role="alert" className="text-danger mt-3 text-sm">
              {resendError}
            </p>
          )}
        </section>

        <section
          className="border-line bg-surface mt-6 rounded-lg border p-6"
          aria-labelledby="deliveries-h"
        >
          <h2 id="deliveries-h" className="text-lg font-semibold">
            סטטוס מסירה
          </h2>

          <h3 className="mt-3 text-sm font-semibold">דוא&quot;ל</h3>
          {emailDeliveries.length === 0 && (
            <p className="text-muted text-sm">אין עדיין תיעוד שליחה.</p>
          )}
          {emailDeliveries.length > 0 && (
            <ul className="mt-1 space-y-1 text-sm">
              {emailDeliveries.map((delivery) => (
                <li key={`${delivery.recipient_kind}-${delivery.to_email}`}>
                  {RECIPIENT_KIND_LABEL[delivery.recipient_kind]} (
                  <span className="ltr-inline">{delivery.to_email}</span>) -{' '}
                  {EMAIL_STATUS_LABEL[delivery.status]}
                  {delivery.attempt_no > 1 && (
                    <span className="text-muted"> · ניסיון {delivery.attempt_no}</span>
                  )}
                  {delivery.error_message && (
                    <span className="text-danger"> - {delivery.error_message}</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          <h3 className="mt-4 text-sm font-semibold">וואטסאפ</h3>
          {whatsappDeliveries.length === 0 && (
            <p className="text-muted text-sm">עדיין לא נשלחה בוואטסאפ ידנית.</p>
          )}
          {whatsappDeliveries.length > 0 && (
            <ul className="mt-1 space-y-1 text-sm">
              {whatsappDeliveries.map((delivery, index) => (
                <li key={`${delivery.to_phone_e164}-${delivery.created_at}-${index}`}>
                  <span className="ltr-inline">{delivery.to_phone_e164}</span> -{' '}
                  {WHATSAPP_STATUS_LABEL[delivery.status]} -{' '}
                  {new Date(delivery.created_at).toLocaleString('he-IL')}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </>
  );
}
